import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Icon from '../../components/AppIcon';

const FAQ = () => {
  const [activeSection, setActiveSection] = useState('usuarios');
  const [openQuestion, setOpenQuestion] = useState(null);

  const userQuestions = [
    {
      id: 1,
      icon: "QrCode",
      question: "¿Cómo aplicar a descuentos en las tiendas?",
      answer: "Al visitar una tienda afiliada, simplemente escanea el código QR proporcionado y el descuento se aplicará automáticamente a tu compra."
    },
    {
      id: 2,
      icon: "CreditCard",
      question: "¿Qué tipos de suscripción existen para los usuarios?",
      answer: "BIZZNET ofrece tres planes de suscripción: Explorador, Emprendedor y Elite. Cada uno se adapta a diferentes necesidades y niveles de ahorro. Mientras más alto el plan, mayores beneficios y menos limitaciones tendrás."
    },
    {
      id: 3,
      icon: "RefreshCw",
      question: "¿Puedo cambiar de plan de suscripción?",
      answer: "Sí. En cualquier momento puedes cambiar tu plan desde tu perfil. El cambio se aplicará al finalizar tu período actual o de forma inmediata según el método de pago seleccionado del sistema."
    },
    {
      id: 4,
      icon: "Users",
      question: "¿Cómo puedo referir amigos y ganar comisiones?",
      answer: "Si tienes un plan Emprendedor o Elite, puedes acceder al Programa de Embajadores BIZZNET. Comparte tu enlace único de referido y gana comisiones por cada persona que se suscriba a través de ti."
    },
    {
      id: 5,
      icon: "MapPin",
      question: "¿En dónde puedo visualizar los descuentos disponibles?",
      answer: "Puedes ver todos los descuentos disponibles en tu zona directamente desde la app. También puedes marcar favoritos y recibir notificaciones de nuevas promociones cerca de ti."
    }
  ];

  const businessQuestions = [
    {
      id: 1,
      icon: "Settings",
      question: "¿Cómo puedo gestionar mis promociones como empresa?",
      answer: "Puedes acceder al panel de control desde nuestra plataforma web para gestionar tus promociones, ver transacciones en tiempo real y personalizar descuentos."
    },
    {
      id: 2,
      icon: "FileText",
      question: "¿Qué necesito para registrarme como empresa en BIZZNET?",
      answer: "Solo necesitas registrarte como empresa en nuestra plataforma y completar los requisitos solicitados: como razón social, NIT, logo, tipo de negocio, contacto, redes sociales y lo más importante configurar un descuento para tus futuros clientes. Una vez aprobada tu solicitud, podrás empezar a ofrecer descuentos a los usuarios BIZZNET."
    },
    {
      id: 3,
      icon: "BarChart3",
      question: "¿Cómo puedo medir el impacto de mis promociones?",
      answer: "Nuestra plataforma ofrece un dashboard completo con métricas en tiempo real: número de redenciones, clientes nuevos, incremento en ventas y más. Podrás descargar reportes detallados para analizar el ROI de tus promociones."
    },
    {
      id: 4,
      icon: "Scan",
      question: "¿Cómo identifico a un usuario de BIZZNET?",
      answer: "En el momento de tu registro en BIZZNET, nuestro usuario te identificará de manera personal escaneando tu código QR el cual usuario se registra en BIZZNET, una vez escaneado tu código QR el sistema lo identifica y es apto para recibir tu descuento."
    }
  ];

  const toggleQuestion = (id) => {
    setOpenQuestion(openQuestion === id ? null : id);
  };

  const currentQuestions = activeSection === 'usuarios' ? userQuestions : businessQuestions;

  return (
    <>
      <Helmet>
        <title>Preguntas Frecuentes - BIZZNET</title>
        <meta name="description" content="Encuentra respuestas a las preguntas más frecuentes sobre BIZZNET. Información para usuarios y empresas sobre descuentos, suscripciones, promociones y más." />
      </Helmet>

      <div className="min-h-screen bg-background">
        {/* Hero Section - FIXED COLOR CONTRAST */}
        <section className="relative bg-gradient-to-br from-blue-900 via-blue-800 to-blue-700 py-12 px-6">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center gap-3 mb-6">
              <Link to="/" className="text-white/90 hover:text-white transition-colors drop-shadow-md">
                <Icon name="ArrowLeft" size={24} />
              </Link>
              <div>
                <h1 className="text-4xl md:text-5xl font-bold text-white drop-shadow-lg">Preguntas Frecuentes</h1>
                <p className="text-white/90 drop-shadow-md">Encuentra respuestas según tu perfil</p>
              </div>
            </div>
            <p className="text-xl md:text-2xl font-medium max-w-3xl text-white/95 drop-shadow-md">
              Obtén respuestas rápidas a las preguntas más comunes sobre BIZZNET
            </p>
          </div>
        </section>

        {/* Section Selector */}
        <section className="py-8 px-6 bg-card border-b border-border">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center gap-3 mb-6">
              <Icon name="HelpCircle" size={32} className="text-primary" />
              <h2 className="text-2xl md:text-3xl font-bold text-foreground">
                Selecciona tu perfil
              </h2>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <button
                onClick={() => setActiveSection('usuarios')}
                className={`flex items-center gap-4 p-6 rounded-xl border-2 transition-all ${
                  activeSection === 'usuarios' ?'bg-primary text-primary-foreground border-primary shadow-lg' :'bg-background text-foreground border-border hover:border-primary/50'
                }`}
              >
                <div className={`p-3 rounded-xl ${
                  activeSection === 'usuarios' ? 'bg-primary-foreground/20' : 'bg-primary/10'
                }`}>
                  <Icon name="User" size={32} className={activeSection === 'usuarios' ? 'text-primary-foreground' : 'text-primary'} />
                </div>
                <div className="text-left">
                  <h3 className="text-xl font-bold mb-1">Preguntas para Usuarios</h3>
                  <p className={`text-sm ${activeSection === 'usuarios' ? 'text-primary-foreground/80' : 'text-muted-foreground'}`}>
                    Descuentos, suscripciones y referidos
                  </p>
                </div>
              </button>

              <button
                onClick={() => setActiveSection('empresas')}
                className={`flex items-center gap-4 p-6 rounded-xl border-2 transition-all ${
                  activeSection === 'empresas' ?'bg-primary text-primary-foreground border-primary shadow-lg' :'bg-background text-foreground border-border hover:border-primary/50'
                }`}
              >
                <div className={`p-3 rounded-xl ${
                  activeSection === 'empresas' ? 'bg-primary-foreground/20' : 'bg-primary/10'
                }`}>
                  <Icon name="Building2" size={32} className={activeSection === 'empresas' ? 'text-primary-foreground' : 'text-primary'} />
                </div>
                <div className="text-left">
                  <h3 className="text-xl font-bold mb-1">Preguntas para Empresas</h3>
                  <p className={`text-sm ${activeSection === 'empresas' ? 'text-primary-foreground/80' : 'text-muted-foreground'}`}>
                    Gestión, registro y análisis
                  </p>
                </div>
              </button>
            </div>
          </div>
        </section>

        {/* FAQ Questions */}
        <section className="py-16 px-6">
          <div className="max-w-4xl mx-auto">
            <div className="space-y-4">
              {currentQuestions?.map((item) => (
                <div 
                  key={item?.id}
                  className="bg-card border border-border rounded-xl overflow-hidden hover:shadow-lg transition-shadow"
                >
                  <button
                    onClick={() => toggleQuestion(item?.id)}
                    className="w-full flex items-center justify-between p-6 text-left hover:bg-muted/50 transition-colors"
                  >
                    <div className="flex items-center gap-4 flex-1">
                      <div className="bg-primary/10 p-3 rounded-xl flex-shrink-0">
                        <Icon name={item?.icon} size={24} className="text-primary" />
                      </div>
                      <h3 className="text-lg font-semibold text-foreground pr-4">
                        {item?.question}
                      </h3>
                    </div>
                    <Icon 
                      name="ChevronDown" 
                      size={24} 
                      className={`text-muted-foreground flex-shrink-0 transition-transform ${
                        openQuestion === item?.id ? 'rotate-180' : ''
                      }`}
                    />
                  </button>
                  
                  {openQuestion === item?.id && (
                    <div className="px-6 pb-6">
                      <div className="pl-16 pr-4 pt-2">
                        <p className="text-muted-foreground leading-relaxed">
                          {item?.answer}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact Support Section */}
        <section className="py-16 px-6 bg-card">
          <div className="max-w-4xl mx-auto text-center">
            <div className="bg-primary/10 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6">
              <Icon name="MessageCircle" size={40} className="text-primary" />
            </div>
            <h2 className="text-3xl font-bold text-foreground mb-4">
              ¿No encontraste lo que buscabas?
            </h2>
            <p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto">
              Nuestro equipo de soporte está disponible para ayudarte con cualquier pregunta adicional que puedas tener.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="mailto:bizznet.latam@gmail.com"
                className="bg-primary text-primary-foreground px-8 py-4 rounded-xl font-semibold hover:bg-primary/90 transition-colors inline-flex items-center justify-center gap-2"
              >
                <Icon name="Mail" size={20} />
                Enviar Correo
              </a>
              <a
                href="tel:+573152495959"
                className="bg-background text-foreground border-2 border-border px-8 py-4 rounded-xl font-semibold hover:border-primary transition-colors inline-flex items-center justify-center gap-2"
              >
                <Icon name="Phone" size={20} />
                Llamar al +57 315 249 5959
              </a>
            </div>
          </div>
        </section>

        {/* CTA Section - ALSO FIXED FOR CONSISTENCY */}
        <section className="py-16 px-6 bg-gradient-to-br from-blue-900 via-blue-800 to-blue-700">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-6 text-white drop-shadow-lg">
              ¿Listo para comenzar?
            </h2>
            <p className="text-lg mb-8 text-white/95 drop-shadow-md">
              Únete a miles de usuarios y empresas que ya están disfrutando de los beneficios de BIZZNET
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/user-registration"
                className="bg-white text-gray-900 px-8 py-4 rounded-xl font-semibold hover:bg-gray-100 transition-colors inline-flex items-center justify-center gap-2 shadow-lg"
              >
                <Icon name="UserPlus" size={20} />
                Registrarse como Usuario
              </Link>
              <Link
                to="/business-registration"
                className="bg-white/10 text-white border-2 border-white/30 px-8 py-4 rounded-xl font-semibold hover:bg-white/20 transition-colors inline-flex items-center justify-center gap-2 backdrop-blur-sm"
              >
                <Icon name="Store" size={20} />
                Registrar Negocio
              </Link>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default FAQ;